import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Trash2, Plus, Clock } from "lucide-react";
import StretchMonthCalendar from "@/components/StretchMonthCalendar";

const BODY_PARTS = [
  { id: "neck", label: "Neck" },
  { id: "shoulder", label: "Shoulders" },
  { id: "upper_back", label: "Upper Back" },
  { id: "lower_back", label: "Lower Back" },
  { id: "wrist", label: "Wrists" },
  { id: "elbow", label: "Elbows" },
  { id: "hand", label: "Hands" },
  { id: "hip", label: "Hips" },
  { id: "full_body", label: "Full Body" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function ManagerStretching() {
  const [list, setList] = useState([]);
  const [historyLimit, setHistoryLimit] = useState(3);
  const [date, setDate] = useState(todayIso());
  const [duration, setDuration] = useState(15);
  const [parts, setParts] = useState([]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/clinic/stretching");
    setList(data || []);
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setParts((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (parts.length === 0) { setErr("Select at least one body part."); return; }
    if (!duration || duration <= 0) { setErr("Duration must be greater than 0."); return; }
    setBusy(true);
    try {
      await api.post("/clinic/stretching/log", {
        body_parts: parts,
        duration_minutes: Number(duration),
        activity_date: date,
        notes,
      });
      setMsg("Session logged.");
      setParts([]); setNotes(""); setDuration(15);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/clinic/stretching/${id}`); load(); };

  return (
    <div>
      <span className="pill">Stretching</span>
      <h1 className="mt-2 text-3xl font-bold">Plan &amp; log clinic stretching</h1>
      <p className="text-zinc-500 mt-1">
        Tap a day on the calendar to schedule a session — every event auto-logs and appears in every subscriber&apos;s work calendar. Use the form below to record sessions that already happened.
      </p>

      {/* Month calendar — click any day to open the schedule dialog */}
      <div className="mt-6">
        <StretchMonthCalendar canEdit={true} onChange={load} showTemperatureTherapy />
      </div>

      {/* Log a past session — kept for ad-hoc backfilling */}
      <form onSubmit={submit} className="card-elev mt-6 space-y-5" data-testid="stretch-log-form">
        <div className="flex items-center gap-2">
          <Plus className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
          <h3 className="font-bold">Log a session that already happened</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold">Date</label>
            <input
              type="date"
              required
              max={todayIso()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="stretch-date"
            />
          </div>
          <div>
            <label className="text-sm font-semibold flex items-center gap-1"><Clock className="w-4 h-4" />Duration (minutes)</label>
            <input
              type="number"
              required
              min="1"
              max="600"
              step="1"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="stretch-duration"
            />
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold mb-2">Body parts</div>
          <div className="flex flex-wrap gap-2">
            {BODY_PARTS.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => toggle(b.id)}
                data-testid={`stretch-part-${b.id}`}
                className={`px-3.5 py-1.5 rounded-full text-sm border transition ${parts.includes(b.id) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-700"}`}
              >{b.label}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold">Notes (optional)</label>
          <textarea
            rows="2"
            placeholder="e.g. led by guest PT, focus on probe-arm release…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-2 w-full rounded-md border border-zinc-200 p-3 text-sm"
            data-testid="stretch-notes"
          />
        </div>

        {err && <div className="text-sm text-red-600" data-testid="stretch-error">{err}</div>}
        {msg && <div className="text-sm text-green-700" data-testid="stretch-success">{msg}</div>}

        <button disabled={busy} className="btn-gold w-full sm:w-auto" data-testid="stretch-log-submit">
          <Plus className="w-4 h-4 mr-2" />{busy ? "Saving\u2026" : "Log session"}
        </button>
      </form>

      {/* All-time session history (auto-logged + backfilled) */}
      <div className="card-elev mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold">Session history</h3>
          <span className="text-xs text-zinc-500">{list.length} total</span>
        </div>
        {list.length === 0 ? (
          <p className="text-sm text-zinc-500">No sessions logged yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500">
                  <tr><th className="py-2">Date</th><th>Duration</th><th>Body parts</th><th>Notes</th><th></th></tr>
                </thead>
                <tbody>
                  {list.slice(0, historyLimit).map((a) => (
                    <tr key={a.activity_id} className="border-t border-zinc-100" data-testid={`stretch-row-${a.activity_id}`}>
                      <td className="py-2">{a.started_at?.slice(0, 10)}</td>
                      <td className="font-semibold">{Math.round(a.duration_minutes || 0)} min</td>
                      <td>{a.body_parts.map((p) => p.replace("_", " ")).join(", ")}</td>
                      <td className="text-zinc-500 max-w-xs truncate">{a.notes || "—"}</td>
                      <td className="text-right">
                        <button onClick={() => remove(a.activity_id)} className="text-red-600" data-testid={`stretch-del-${a.activity_id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.length > historyLimit && (
              <div className="mt-4 flex justify-center gap-3">
                <button onClick={() => setHistoryLimit((n) => n + 10)} className="btn-outline text-sm h-9 px-4" data-testid="stretch-history-load-more">
                  Load 10 more · {list.length - historyLimit} hidden
                </button>
                <button onClick={() => setHistoryLimit(list.length)} className="btn-ghost text-sm h-9 px-4" data-testid="stretch-history-show-all">
                  Show all
                </button>
              </div>
            )}
            {historyLimit > 3 && list.length > 3 && (
              <div className="mt-2 flex justify-center">
                <button onClick={() => setHistoryLimit(3)} className="btn-ghost text-xs h-8" data-testid="stretch-history-collapse">
                  Collapse to 3
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
